import { AxiosError, AxiosResponse } from 'axios'
import { camelize, snakeize } from 'casing'
import {
  startLoading,
  stopLoading,
} from '../../../common/modules/loading/actions'
import { LoadingTypes } from '../../../common/modules/loading/reducer'
import { MappingManagerTemplateStatusType } from '../../../oppsync/modules/coSellMappingManager/reducer'
import { AppDispatch, RootState } from '../../../store'
import {
  MarketplaceFieldMapping,
  MarketplaceFieldMappingDimensions,
  MarketplaceFieldMappingMetadata,
  MarketplaceHubspotFieldsData,
  MarketplaceSalesforceFieldsData,
  MarketplaceTemplateDataResponse,
  MarketplaceTemplatesDataFieldMappings,
  MarketplaceTemplatesDetailsData,
  ProductsWithDimensions,
} from './reducer'
import { isEmpty } from 'lodash'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { getErrorMessages } from '../../../common/utils/error'
import {
  ReferralTemplateResetSuccess,
  ReferralTemplateSaveSuccess,
  ReferralTemplateCreateFailureMessage,
  RequestFailureMessage,
} from '../../../common/utils/messagesContants'
import { errorLogger } from '../../../common/utils/errorLogger'
import {
  deleteSingleReferralTemplate,
  getAllMarketplaceTemplatesSalesforce,
  getAllReferralTemplates,
  getHubspotFieldsForTemplate,
  getReferralTemplate,
  getReferralTemplateForSalesforce,
  postReferralTemplateFromMappingService,
  postCloneReferralTemplate,
  postReferralTemplate,
  putReferralTemplate,
  resetReferralTemplate,
  getSalesforceFieldsForTemplate,
  putReferralTemplateFromMappingService,
  getProductsWithDimensions,
  postCloneTemplateSalesforce,
  deleteTemplateSalesforce,
} from '../../../common/api/mappingManager'
import { UserState } from '../../../oppsync/modules/userList/reducer'
import { CRMTypes } from '../../../common/utils/constants'
import { flattenSalesforceFields } from '../../../oppsync/modules/coSellMappingManager/action'

export enum MarketplaceMappingManagerActionTypes {
  SET_TEMPLATE_DATA = 'SET_TEMPLATE_DATA',
  SET_TEMPLATE_AS_DRAFT = 'SET_TEMPLATE_AS_DRAFT',
  SET_MARKETPLACE_TEMPLATE_DATA_INFO = 'SET_MARKETPLACE_TEMPLATE_DATA_INFO',
  SET_HUBSPOT_FIELDS = 'SET_HUBSPOT_FIELDS',
  UPDATE_TEMPLATE_SECTION_FIELD = 'UPDATE_TEMPLATE_SECTION_FIELD',
  SET_SALESFORCE_FIELDS = 'SET_SALESFORCE_FIELDS',
  SET_PRODUCTS_WITH_DIMENSIONS = 'SET_PRODUCTS_WITH_DIMENSIONS',
}

export const setMarketplaceTemplatesInfo = (
  referralTemplateDataInfo: MarketplaceTemplatesDetailsData[]
) => ({
  type: MarketplaceMappingManagerActionTypes.SET_MARKETPLACE_TEMPLATE_DATA_INFO,
  payload: referralTemplateDataInfo,
})

export const setMarketplaceReferralTemplate = (
  referralTemplateData: MarketplaceTemplateDataResponse
) => ({
  type: MarketplaceMappingManagerActionTypes.SET_TEMPLATE_DATA,
  payload: referralTemplateData,
})

export const setMarketplaceTemplateAsDraft = (isDraft: boolean) => ({
  type: MarketplaceMappingManagerActionTypes.SET_TEMPLATE_AS_DRAFT,
  payload: isDraft,
})

export const setAllHubspotFields = (
  fieldsData: MarketplaceHubspotFieldsData[]
) => ({
  type: MarketplaceMappingManagerActionTypes.SET_HUBSPOT_FIELDS,
  payload: fieldsData,
})

export const setAllSalesforceFields = (
  fieldsData: MarketplaceSalesforceFieldsData[]
) => ({
  type: MarketplaceMappingManagerActionTypes.SET_SALESFORCE_FIELDS,
  payload: fieldsData,
})

export const updateMarketplaceTemplateSectionField = (
  fieldKey: keyof MarketplaceTemplatesDataFieldMappings,
  updates:
    | Partial<MarketplaceFieldMapping>
    | Partial<MarketplaceFieldMappingMetadata>
    | Partial<MarketplaceFieldMappingDimensions>
) => ({
  type: MarketplaceMappingManagerActionTypes.UPDATE_TEMPLATE_SECTION_FIELD,
  payload: { fieldKey, updates },
})

export const mapTemplateDataToDetails = (
  templates: any[],
  users: UserState[]
): MarketplaceTemplatesDetailsData[] =>
  templates.map(template => {
    const user = users.find(user => user.id === template.createdBy)
    const createdByName = user?.name || '-'

    return {
      templateName: template.templateName,
      templateId: template.id,
      templateType: template.templateType || '-',
      status: template.isDraft
        ? MappingManagerTemplateStatusType.DRAFT
        : MappingManagerTemplateStatusType.ACTIVE,
      createdBy: createdByName,
      creationDate: template.createdAt,
      lastUpdatedAt: template.updatedAt,
      cloudProvider: template.cloudProvider,
    }
  })

export const getMarketplaceReferralTemplatesInfo =
  (
    crmId: string,
    templateType?: string,
    crmtype?: string,
    accountId?: string,
    loading?: boolean,
    isActive?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const effectiveTemplateType =
      templateType ??
      (crmtype === CRMTypes.salesforce ? 'marketplace' : 'flyout')
    if (!loading) {
      await dispatch(startLoading(LoadingTypes.FETCH_REFERRAL_TEMPLATES))
    }
    try {
      if (crmtype === CRMTypes.salesforce && accountId) {
        const response = await getAllMarketplaceTemplatesSalesforce(
          crmId,
          accountId,
          effectiveTemplateType,
          isActive
        )

        if (!isEmpty(response?.data)) {
          const camelizedResponse = camelize(response.data) as any
          const templates = camelizedResponse?.mappingTemplates || []
          const usersData = getState()?.userList.user.userList.users || []

          const referralTemplatesData: MarketplaceTemplatesDetailsData[] =
            templates.map((template: any) => {
              const user = usersData.find(
                (user: UserState) => user.id === template.createdBy
              )
              const createdByName = user?.name || '-'

              return {
                templateName: template.name,
                templateId: template.id,
                templateType: template.templateType || '-',
                status: template.isDraft
                  ? MappingManagerTemplateStatusType.DRAFT
                  : MappingManagerTemplateStatusType.ACTIVE,
                createdBy: createdByName,
                creationDate: template.createdAt,
                cloudProvider: template.cloudProvider,
                lastUpdatedAt: template.updatedAt || template.createdAt,
              }
            })
          await dispatch(setMarketplaceTemplatesInfo(referralTemplatesData))
        }
      } else {
        const response = await getAllReferralTemplates(
          crmId,
          isActive,
          effectiveTemplateType,
          crmtype
        )

        if (!isEmpty(response?.data)) {
          const templateDetailsData = camelize(response?.data?.data?.templates)
          const usersData = getState()?.userList.user.userList.users
          const referralTemplatesData = mapTemplateDataToDetails(
            templateDetailsData,
            usersData
          )
          await dispatch(setMarketplaceTemplatesInfo(referralTemplatesData))
        }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (!loading)
        await dispatch(stopLoading(LoadingTypes.FETCH_REFERRAL_TEMPLATES))
    }
  }

export const createMarketplaceReferralTemplate =
  (templateData: Record<string, string>, crmId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await dispatch(startLoading(LoadingTypes.CREATE_REFERRAL_TEMPLATE))
      const response = await postReferralTemplate(crmId, snakeize(templateData))
      if (!isEmpty(response?.data)) {
        const templateDataResponse = camelize(response?.data?.data)
        const templateId = templateDataResponse?.id
        await dispatch(setMarketplaceReferralTemplate(templateDataResponse))
        return { templateId, errorMessage: '' }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const errorMessage = getErrorMessages([
        ReferralTemplateCreateFailureMessage,
      ])(errorResponse)
      dispatch(
        updateAppAlert({
          message: getErrorMessages([ReferralTemplateCreateFailureMessage])(
            errorResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { templateId: '', errorMessage }
    } finally {
      await dispatch(stopLoading(LoadingTypes.CREATE_REFERRAL_TEMPLATE))
    }
    return {
      templateId: '',
      errorMessage: 'Failed to create template. Please try again.',
    }
  }

export const createMarketplaceSalesforceTemplate =
  (
    templateData: Record<string, unknown>,
    crmId: string,
    accountId: string,
    useTemplateFromMappingService?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await dispatch(startLoading(LoadingTypes.CREATE_REFERRAL_TEMPLATE))
      const useMappingOn = useTemplateFromMappingService ?? false
      const response =
        useMappingOn && accountId
          ? await postReferralTemplateFromMappingService(
              crmId,
              snakeize(templateData),
              accountId
            )
          : await postReferralTemplate(crmId, snakeize(templateData))
      if (!isEmpty(response?.data)) {
        const templateDataResponse = camelize(response?.data)
        const templateId = templateDataResponse?.id
        return { templateId, errorMessage: '' }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const apiMessage =
        (errorResponse as unknown as { data?: any })?.data?.message ||
        (errorResponse as unknown as { data?: any })?.data?.error ||
        (errorResponse as unknown as { data?: any })?.data?.detail

      const errorMessage = apiMessage
        ? String(apiMessage)
        : getErrorMessages([RequestFailureMessage])(errorResponse)
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { templateId: '', errorMessage }
    } finally {
      await dispatch(stopLoading(LoadingTypes.CREATE_REFERRAL_TEMPLATE))
    }
    return {
      templateId: '',
      errorMessage: 'Failed to create template. Please try again.',
    }
  }

const cloneMarketplaceTemplateAPI = async ({
  crmId,
  templateId,
  templateData,
  crmType,
  accountId,
}: {
  crmId: string
  templateId: string
  crmType?: CRMTypes
  templateData: Record<string, string>
  accountId?: string
}) => {
  if (crmType === CRMTypes.salesforce) {
    return await postCloneTemplateSalesforce(
      crmId,
      accountId ?? '',
      snakeize(templateData)
    )
  }

  return await postCloneReferralTemplate(
    crmId,
    templateId,
    snakeize(templateData)
  )
}

export const cloneMarketplaceReferralTemplate =
  (
    templateData: Record<string, string>,
    crmId: string,
    templateId: string,
    accountId?: string,
    crmType?: CRMTypes
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await dispatch(startLoading(LoadingTypes.CLONE_REFERRAL_TEMPLATE))
      const response = await cloneMarketplaceTemplateAPI({
        crmId,
        templateId,
        templateData,
        crmType,
        accountId,
      })
      if (!isEmpty(response?.data)) {
        const templateDataResponse =
          crmType === CRMTypes.salesforce
            ? camelize(response?.data)
            : camelize(response?.data?.data)
        const newTemplateId = templateDataResponse?.id
        await dispatch(setMarketplaceReferralTemplate(templateDataResponse))
        return { templateId: newTemplateId, errorMessage: '' }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const errorMessage = getErrorMessages([RequestFailureMessage])(
        errorResponse
      )
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { templateId: '', errorMessage }
    } finally {
      await dispatch(stopLoading(LoadingTypes.CLONE_REFERRAL_TEMPLATE))
    }
    return {
      templateId: '',
      errorMessage: 'Failed to clone template. Please try again.',
    }
  }

const deleteMarketplaceTemplateAPI = async ({
  crmId,
  templateId,
  crmType,
  accountId,
}: {
  crmId: string
  templateId: string
  crmType?: CRMTypes
  accountId?: string
}) => {
  if (crmType === CRMTypes.salesforce) {
    return await deleteTemplateSalesforce(crmId, accountId ?? '', templateId)
  }

  return await deleteSingleReferralTemplate(crmId, templateId)
}
export const deleteMarketplaceReferralTemplate =
  (
    crmId: string,
    templateId: string,
    templateName: string,
    accountId?: string,
    crmType?: CRMTypes
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.DELETE_REFERRAL_TEMPLATE))
    try {
      await deleteMarketplaceTemplateAPI({
        crmId,
        templateId,
        accountId,
        crmType,
      })
      await dispatch(
        updateAppAlert({
          message: `${templateName} deleted successfully.`,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([`${templateName} deletion failed.`])(
            errorResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.DELETE_REFERRAL_TEMPLATE))
    }
  }

export const resetMarketplaceReferralTemplate =
  (crmId: string, templateId: string, accountId?: string, crmType?: CRMTypes) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    try {
      const templateDataResponse =
        getState()?.marketplaceMappingManager?.templateData?.templateData
      const response =
        crmType === CRMTypes.salesforce
          ? await putReferralTemplateFromMappingService(
              crmId,
              templateId,
              snakeizeMarketplaceReferralTemplate({
                ...templateDataResponse,
                resetToDefault: true,
              }),
              accountId ?? ''
            )
          : await resetReferralTemplate(
              crmId,
              templateId,
              snakeize(templateDataResponse)
            )
      if (!isEmpty(response?.data)) {
        await dispatch(
          updateAppAlert({
            message: ReferralTemplateResetSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        const currentReferralTemplateData = camelize(
          crmType === CRMTypes.salesforce
            ? response?.data
            : response?.data?.data
        )
        await dispatch(
          setMarketplaceReferralTemplate(currentReferralTemplateData)
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    }
  }

export const updateMarketplaceReferralTemplateName =
  (
    crmId: string,
    templateId: string,
    templateData: Partial<MarketplaceTemplateDataResponse>,
    accountId?: string,
    useTemplateFromMappingService?: boolean,
    isSalesforce?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.EDIT_REFERRAL_TEMPLATE_NAME))
    try {
      const useMappingOn = useTemplateFromMappingService ?? false
      const response =
        useMappingOn && accountId
          ? await putReferralTemplateFromMappingService(
              crmId,
              templateId,
              snakeize(templateData),
              accountId
            )
          : await putReferralTemplate(crmId, templateId, snakeize(templateData))
      if (!isEmpty(response?.data)) {
        await dispatch(
          updateAppAlert({
            message: ReferralTemplateSaveSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        const currentReferralTemplateData = camelize(
          isSalesforce ? response?.data : response?.data?.data
        )
        await dispatch(
          setMarketplaceReferralTemplate(currentReferralTemplateData)
        )
        return true
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return false
    } finally {
      await dispatch(stopLoading(LoadingTypes.EDIT_REFERRAL_TEMPLATE_NAME))
    }
  }

export const snakeizeMarketplaceReferralTemplate = (
  template: MarketplaceTemplateDataResponse
) => {
  return {
    ...snakeize(template),
    field_mappings: {
      fields: {
        fields_mapping: snakeizeKeyValueObject(
          template?.fieldMappings?.fields?.fieldsMapping ?? {}
        ),
      },
      installments: {
        installments_mapping: snakeizeKeyValueObject(
          template?.fieldMappings?.installments?.installmentsMapping ?? {}
        ),
      },
      metadata: {
        metadata_mapping: snakeizeKeyValueObject(
          template?.fieldMappings?.metadata?.metadataMapping ?? {}
        ),
      },
      recipient_source: {
        recipient_source_mapping: snakeizeKeyValueObject(
          template?.fieldMappings?.recipientSource?.recipientSourceMapping ?? {}
        ),
      },
      dimensions: {
        dimensions_mapping: {
          ...Object.fromEntries(
            Object.entries(
              template?.fieldMappings?.dimensions?.dimensionsMapping ?? {}
            ).map(([key, value]) => {
              const updatedValue = {
                ...snakeize(value as Record<string, unknown>),
                dimensions: {
                  ...Object.fromEntries(
                    Object.entries(
                      (value as Record<string, unknown>)?.dimensions as Record<
                        string,
                        unknown
                      >
                    ).map(([key, value]) => {
                      return [key, snakeize(value)]
                    })
                  ),
                },
              }
              return [key, updatedValue]
            })
          ),
        },
      },
    },
  }
}

export const getMarketplaceReferralTemplate =
  (crmId: string, templateId: string, hasDiscared?: boolean) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (hasDiscared) {
      await dispatch(startLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    } else {
      await dispatch(startLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE))
    }
    try {
      const response = await getReferralTemplate(crmId, templateId)
      if (!isEmpty(response?.data)) {
        const currentReferralTemplateData = camelize(response?.data?.data)
        await dispatch(
          setMarketplaceReferralTemplate(currentReferralTemplateData)
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (hasDiscared) {
        await dispatch(stopLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
      } else {
        await dispatch(
          stopLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE)
        )
      }
    }
  }

export const getAllSalesforceFields =
  (crmId: string, accountId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_SALESFORCE_FIELDS))
    try {
      const response = await getSalesforceFieldsForTemplate(
        crmId,
        accountId,
        'Opportunity'
      )
      if (!isEmpty(response?.data)) {
        const crmFieldData = camelize(response?.data?.fields)
        const updatedFields = flattenSalesforceFields(crmFieldData)
        await dispatch(setAllSalesforceFields(updatedFields))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_SALESFORCE_FIELDS))
    }
  }

export const getMarketplaceReferralTemplateSalesforce =
  (
    crmId: string,
    accountId: string,
    templateId: string,
    hasDiscared?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (hasDiscared) {
      await dispatch(startLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    } else {
      await dispatch(startLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE))
    }
    try {
      const response = await getReferralTemplateForSalesforce(
        crmId,
        accountId,
        templateId
      )
      if (!isEmpty(response?.data)) {
        const currentReferralTemplateData = {
          ...camelize(response?.data),
          fieldMappings: {
            ...camelize(response?.data?.field_mappings ?? {}),
            fields: {
              fieldsMapping: {
                ...Object.fromEntries(
                  Object.entries(
                    response?.data?.field_mappings?.fields?.fields_mapping ?? {}
                  ).map(([key, value]) => {
                    return [key, camelize(value)]
                  })
                ),
              },
            },
            installments: {
              installmentsMapping: {
                ...camelizeKeyValueObject(
                  response?.data?.field_mappings?.installments
                    ?.installments_mapping ?? {}
                ),
              },
            },
            metadata: {
              metadataMapping: {
                ...camelizeKeyValueObject(
                  response?.data?.field_mappings?.metadata?.metadata_mapping ??
                    {}
                ),
              },
            },
            recipientSource: {
              recipientSourceMapping: {
                ...camelizeKeyValueObject(
                  response?.data?.field_mappings?.recipient_source
                    ?.recipient_source_mapping ?? {}
                ),
              },
            },
            dimensions: {
              dimensionsMapping: {
                ...Object.fromEntries(
                  Object.entries(
                    response?.data?.field_mappings?.dimensions
                      ?.dimensions_mapping ?? {}
                  ).map(([key, value]) => {
                    const updatedValue = {
                      ...camelize(value as Record<string, unknown>),
                      dimensions: {
                        ...Object.fromEntries(
                          Object.entries(
                            (value as Record<string, unknown>)
                              ?.dimensions as Record<string, unknown>
                          ).map(([key, value]) => {
                            return [key, camelize(value)]
                          })
                        ),
                      },
                    }
                    return [key, updatedValue]
                  })
                ),
              },
            },
          },
        }
        await dispatch(
          setMarketplaceReferralTemplate(
            currentReferralTemplateData as MarketplaceTemplateDataResponse
          )
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (hasDiscared) {
        await dispatch(stopLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
      } else {
        await dispatch(
          stopLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE)
        )
      }
    }
  }

export const saveMarketplaceReferralTemplate =
  (
    crmId: string,
    templateId: string,
    accountId?: string,
    useTemplateFromMappingService?: boolean,
    crmType?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.SAVE_REFERRAL_TEMPLATE))
    try {
      const templateDataResponse =
        getState()?.marketplaceMappingManager.templateData.templateData
      const currentUserId = getState()?.userProfile?.userProfile?.id
      templateDataResponse.updatedBy = currentUserId
      // Marketplace: use mapping-configuration service for Salesforce templates.
      // Otherwise, fall back to legacy CRM referral-template API.
      const shouldUseMappingService =
        crmType === CRMTypes.salesforce
          ? !!accountId
          : (useTemplateFromMappingService ?? false) && !!accountId
      const response =
        shouldUseMappingService && accountId
          ? await putReferralTemplateFromMappingService(
              crmId,
              templateId,
              snakeizeMarketplaceReferralTemplate(templateDataResponse),
              accountId
            )
          : await putReferralTemplate(
              crmId,
              templateId,
              snakeize(templateDataResponse)
            )

      if (!isEmpty(response?.data)) {
        dispatch(
          updateAppAlert({
            message: ReferralTemplateSaveSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        const currentReferralTemplateData = camelize(response?.data?.data)
        await dispatch(
          setMarketplaceReferralTemplate(currentReferralTemplateData)
        )
        return true
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return false
    } finally {
      await dispatch(stopLoading(LoadingTypes.SAVE_REFERRAL_TEMPLATE))
    }
  }

export const getAllMarketplaceHubspotFields =
  (crmId: string, objectType?: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_HUBSPOT_FIELDS))
    try {
      const response = await getHubspotFieldsForTemplate(crmId, objectType)
      if (!isEmpty(response?.data)) {
        const crmFieldData = camelize(response?.data?.data?.fields)
        await dispatch(setAllHubspotFields(crmFieldData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_HUBSPOT_FIELDS))
    }
  }

export const setProductsWithDimensions = (
  products: ProductsWithDimensions[]
) => ({
  type: MarketplaceMappingManagerActionTypes.SET_PRODUCTS_WITH_DIMENSIONS,
  payload: { products },
})

export const getMarketplaceProductsWithDimensions =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PRODUCTS_WITH_DIMENSIONS))
    try {
      const response = await getProductsWithDimensions(partnerId)
      if (!isEmpty(response?.data)) {
        const products = camelize(response?.data?.products)
        await dispatch(
          setProductsWithDimensions(products as ProductsWithDimensions[])
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PRODUCTS_WITH_DIMENSIONS))
    }
  }

export const camelizeKeyValueObject = (
  keyValueObject: Record<string, unknown>
) => {
  return Object.fromEntries(
    Object.entries(keyValueObject).map(([key, value]) => {
      return [key, camelize(value)]
    })
  )
}

export const snakeizeKeyValueObject = (
  keyValueObject: Record<string, unknown>
) => {
  return Object.fromEntries(
    Object.entries(keyValueObject).map(([key, value]) => {
      return [key, snakeize(value)]
    })
  )
}
